import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import config from '../config.json';
import logo from '../assets/quickLogo.png';
import './navbar.css';
import { DOC_ROUTES } from '../utils/constants';


const NAV_LINKS = [
    { to: '/', label: 'HOME' },
    { to: '/features', label: 'FEATURES' },
    { to: '/contact', label: 'CONTACT' },
    { to: '/get-started', label: 'DOCS', activeOn: DOC_ROUTES },
    { to: '/pricing', label: 'PRICING' },
];

const LOGIN_URL = `${config.CUSTOMER_WEBSITE_URL}/login`;
const REGISTER_URL = `${config.CUSTOMER_WEBSITE_URL}/register`;

// A link with `activeOn` is active for any path in that list; otherwise it
// falls back to React Router v5's default exact match.
const isLinkActive = (link) =>
    link.activeOn ? (match, location) => link.activeOn.includes(location.pathname) : undefined;

function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Close the menu whenever the route changes (covers in-app navigation).
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // While open: lock body scroll and allow Escape to close.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = open ? 'hidden' : '';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };
    }, [open]);

    // If the viewport grows to desktop while the menu is open, close it so the
    // mobile overlay never lingers behind the desktop layout.
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const onChange = (e) => {
            if (e.matches) setOpen(false);
        };
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return (
        <nav className={`wqnav${open ? ' wqnav-open' : ''}`}>
            <div className="wqnav-inner">
                {/* Logo */}
                <Link to="/" className="wqnav-brand" onClick={() => setOpen(false)}>
                    <img src={logo} alt="WayaQuick" className="wqnav-logo" />
                </Link>

                {/* Desktop links */}
                <div className="wqnav-links">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            exact={!link.activeOn}
                            isActive={isLinkActive(link)}
                            className="wqnav-link"
                            activeClassName="wqnav-link-active"
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </div>

                {/* Desktop actions */}
                <div className="wqnav-actions">
                    <a href={LOGIN_URL} className="wqnav-login">
                        LOGIN
                    </a>
                    <a href={REGISTER_URL} className="wqnav-register">
                        REGISTER
                    </a>
                </div>

                {/* Hamburger (mobile only) */}
                <button
                    type="button"
                    className={`wqnav-burger${open ? ' wqnav-burger-open' : ''}`}
                    aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
                    aria-expanded={open}
                    aria-controls="wqnav-menu"
                    onClick={() => setOpen((v) => !v)}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </div>

            {/* Backdrop */}
            <div
                className={`wqnav-backdrop${open ? ' wqnav-backdrop-open' : ''}`}
                onClick={() => setOpen(false)}
                aria-hidden="true"
            />

            {/* Mobile menu */}
            <div id="wqnav-menu" className={`wqnav-menu${open ? ' wqnav-menu-open' : ''}`}>
                <div className="wqnav-menu-body">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            exact={!link.activeOn}
                            isActive={isLinkActive(link)}
                            className="wqnav-menu-link"
                            activeClassName="wqnav-menu-link-active"
                            onClick={() => setOpen(false)}
                        >
                            {link.label}
                        </NavLink>
                    ))}

                    <div className="wqnav-menu-actions">
                        <a href={LOGIN_URL} className="wqnav-menu-login">
                            LOGIN
                        </a>
                        <a href={REGISTER_URL} className="wqnav-menu-register">
                            REGISTER
                        </a>
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;